import { useState } from 'react'

import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import * as spriteIcons from 'assets/svg/sprite'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { useModalContext } from 'context/modalContext'

import AvatarHolder from '../AvatarHolder/AvatarHolder'
import './_userCard.scss'

interface UserCardEarningsProps {
    user: any
    earningsCardType: string
    userCardSubType: string
    hasDropDown?: boolean
    dropDownContent?: JSX.Element | JSX.Element[]
    customIconClass?: string
    iconSize?: string
    created?: string
    earnings?: number
    refunded?: any
    chargeback?: number
}

const UserCardEarnings = ({
    user,
    earningsCardType,
    userCardSubType,
    hasDropDown,
    dropDownContent,
    customIconClass,
    iconSize,
    created,
    earnings,
    refunded,
    chargeback
}: UserCardEarningsProps) => {
    const [dropDownOpen, setDropDownOpen] = useState(false)
    const [showFullDate, setShowFullDate] = useState(false)
    const { t } = useTranslation()
    const modalData = useModalContext()

    const handleOpenUserDetails = () => {
        const normalizedUser = {
            ...user,
            avatar: {
                url: user?.avatar?.url,
            },
        }

        modalData.addModal('', <UserDetails user={normalizedUser} />, true)
    }

    const renderBadge = () => {
        const badgeTypeFn = () => {
            if (earningsCardType === t('subscription') && refunded === 1) {
                return 'typeBadge--orange'
            } else if (earningsCardType === t('subscription')) {
                return 'typeBadge--blue'
            } else if (earningsCardType.includes('tipp')) {
                return 'typeBadge--green'
            } else if (earningsCardType === t('processing')) {
                return 'typeBadge--blue'
            } else if (earningsCardType === t('paid')) {
                return 'typeBadge--green'
            } else if (earningsCardType === t('post')) {
                return 'typeBadge--yellow'
            } else if (earningsCardType === t('message')) {
                return 'typeBadge--pink'
            } else if (earningsCardType === t('failed')) {
                return 'typeBadge--red'
            } else if (earningsCardType === t('chargebacks')) {
                return 'typeBadge--red'
            } else if (earningsCardType === 'post_goal') {
                return 'typeBadge--green'
            } else if (earningsCardType === t('referral')) {
                return 'typeBadge--lime'
            }
        }

        const badgeTextFn = () => {
            if (['tipp', 'post_goal'].some(type => earningsCardType.includes(type))) {
            return t('tip')
            }
            if (earningsCardType === t('subscription') && refunded === 1) {
                return t('Refund')
            }
            return earningsCardType
        }

        return (
            <div className={`typeBadge ${badgeTypeFn()}`}>
                <span>{badgeTextFn()}</span>
            </div>
        )
    }

    const renderDate = () => {
        if (!created) return null

        const dateObj = dayjs(created)
        const isCurrentYear = dateObj.year() === dayjs().year()

        // If toggled, always show full format (no leading 0)
        if (showFullDate) {
            return dateObj.format('MMM DD, YYYY h:mm A')
        }

        // Default view: hide year if same year, no leading 0
        return isCurrentYear
            ? dateObj.format('MMM DD h:mm A')
            : dateObj.format('MMM DD, YYYY h:mm A')
    }

    return (
        <div className='userCard userCard--earnings'>
            <div className='userCard--earnings--content'>
                <div className='userCard__avatar' onClick={handleOpenUserDetails} style={{ cursor: 'pointer' }}>
                    <AvatarHolder
                        img={user?.avatar?.url}
                        size={iconSize ?? '80'}
                        customClass={customIconClass ?? ''}
                    />
                </div>

                {userCardSubType === 'sale' ? (
                    <div className='userCard__info'>
                        <p className='userCard__info__name'>{user.name}</p>
                        {renderBadge()}
                    </div>
                ) : null}

                {userCardSubType === 'payout' ? (
                    <div className='userCard__info'>
                        <p className='userCard__info__name'>
                            <span>{t('invoice')}</span> #456712
                        </p>
                        {renderBadge()}
                    </div>
                ) : null}

                <div className='earnings-sub'>
                    <div className='chargeback'>
                        {chargeback === 1 ? <span>!</span> : ''}
                        <p className='earnings-sub--money'>${earnings}</p>
                    </div>

                    <p
                        className='earnings-sub--date'
                        onClick={() => setShowFullDate(!showFullDate)}
                        style={{ cursor: 'pointer' }}
                        title={dayjs(created).format('MMM DD, YYYY hh:mm A')}
                    >
                        {renderDate()}
                    </p>
                </div>

                {hasDropDown && (
                    <button
                        className={`earnings-dropdown-button ${dropDownOpen ? 'earnings-dropdown-button-open' : ''}`}
                        onClick={() => setDropDownOpen(!dropDownOpen)}
                    >
                        <div className='earnings-dropdown-button-icon'>
                            <spriteIcons.IconDownChevron />
                        </div>
                    </button>
                )}
            </div>

            {hasDropDown && (
                <div className={`earnings-dropdown ${dropDownOpen ? 'earnings-dropdown-open' : ''}`}>
                    {dropDownContent ?? t('noContent')}
                </div>
            )}
        </div>
    )
}

export default UserCardEarnings
